import { API_BASE_URL } from '@/lib/constants'
import { useAuthStore } from '@/stores/auth-store'

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
    message?: string,
  ) {
    super(message ?? `API error ${status}`)
    this.name = 'ApiError'
  }
}

export interface ApiErrorBody {
  message: string
  errors?: Record<string, string[]>
}

type ParamValue = string | number | boolean | undefined
type ParamArrayValue = number[] | string[]

interface RequestOptions {
  params?: Record<string, ParamValue | ParamArrayValue>
  body?: unknown
  signal?: AbortSignal
}

async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const { token } = useAuthStore.getState()

  const url = new URL(`${API_BASE_URL}${path}`, window.location.origin)
  if (options.params) {
    for (const [key, value] of Object.entries(options.params)) {
      if (value === undefined) continue
      if (Array.isArray(value)) {
        for (const item of value) {
          url.searchParams.append(`${key}[]`, String(item))
        }
      } else {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
  }

  if (token !== null) {
    headers.Authorization = `Bearer ${token}`
  }

  const isFormData = options.body instanceof FormData
  if (options.body !== undefined && !isFormData) {
    headers['Content-Type'] = 'application/json'
  }

  const fetchBody: FormData | string | undefined =
    options.body === undefined
      ? undefined
      : isFormData
        ? (options.body as FormData)
        : JSON.stringify(options.body)

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: fetchBody,
    signal: options.signal,
  })

  if (response.status === 401) {
    if (token !== null) {
      useAuthStore.getState().clearAuth()
      window.location.href = '/login'
    }
    const body = (await response.json()) as ApiErrorBody
    throw new ApiError(401, body, body.message)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const body: unknown = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody, (body as ApiErrorBody).message)
  }

  return body as T
}

export const api = {
  get: async <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: async <T>(path: string, options?: RequestOptions) => request<T>('POST', path, options),
  put: async <T>(path: string, options?: RequestOptions) => request<T>('PUT', path, options),
  patch: async <T>(path: string, options?: RequestOptions) => request<T>('PATCH', path, options),
  delete: async <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
}
